import type { Point } from 'shared/domain/area/types';
import { isNonEmptyArray } from 'shared/utils/array';
import type { MapCoordinates } from 'shared/types/mapCoordinates';

export enum IssueLocationType {
  pin = 'pin',
  area = 'area',
}

export function isAreaLocationType(type: IssueLocationType): boolean {
  return type === IssueLocationType.area;
}

type Area = Point[];

/** The map version an issue was located on (PT-7117): the documentation and the version the user saw. */
export interface LocalizedOnMap {
  documentationId: string;
  versionId: string;
}

/**
 * Where the issue is on its level's map: a pin or areas, on the map version
 * the user saw. `null` on the issue: not located (the backend sets no default pin).
 */
export interface IssueLocationModel {
  selectedLocationType: IssueLocationType;
  positionOnMap?: MapCoordinates;
  targetAreas?: Area[];
  finalAreas?: Area[];
  localizedOnMap: LocalizedOnMap;
}

/** The location as views and forms edit it: one field per kind of placement, and the map version it is on. */
export type IssueLocationFields = {
  selectedLocationType: IssueLocationType;
  positionOnMap: MapCoordinates | undefined;
  targetAreas: Area[];
  finalAreas: Area[];
  /** `undefined`: the issue is not located. */
  localizedOnMap: LocalizedOnMap | undefined;
};

export function issueLocationToFields(location: IssueLocationModel | null | undefined): IssueLocationFields {
  return {
    selectedLocationType: location?.selectedLocationType ?? IssueLocationType.pin,
    positionOnMap: location?.positionOnMap,
    targetAreas: location?.targetAreas ?? [],
    finalAreas: location?.finalAreas ?? [],
    localizedOnMap: location?.localizedOnMap,
  };
}

/** Only the location fields, e.g. for a copy of the issue on the same level: on the same map version. */
export function pickIssueLocationFields(fields: IssueLocationFields): IssueLocationFields {
  const { selectedLocationType, positionOnMap, targetAreas, finalAreas, localizedOnMap } = fields;
  return { selectedLocationType, positionOnMap, targetAreas, finalAreas, localizedOnMap };
}

/**
 * The location to save when it is confirmed on another map version (PT-7117): the
 * same pin or areas, now said to be in the right place on that version; `null` when
 * the issue has no placement to confirm.
 */
export function toConfirmedIssueLocation(fields: IssueLocationFields, version: LocalizedOnMap): IssueLocationModel | null {
  return toIssueLocation({ ...fields, localizedOnMap: version });
}

/**
 * Whether the fields place the issue on the map: a pin needs its position,
 * areas need at least one area (the backend's `issueLocationWithoutPlacement`).
 */
export function hasPlacement(fields: IssueLocationFields): boolean {
  const { selectedLocationType, positionOnMap, targetAreas, finalAreas } = fields;
  return isAreaLocationType(selectedLocationType)
    ? isNonEmptyArray(targetAreas) || isNonEmptyArray(finalAreas)
    : Boolean(positionOnMap);
}

/**
 * The location to save: only the placement of its type, on the map version
 * the user saw. `null` without that version or without a placement: there is
 * no location to save.
 */
export function toIssueLocation(fields: IssueLocationFields): IssueLocationModel | null {
  const { selectedLocationType, positionOnMap, targetAreas, finalAreas, localizedOnMap } = fields;
  if (!localizedOnMap || !hasPlacement(fields)) {
    return null;
  }
  return isAreaLocationType(selectedLocationType)
    ? { selectedLocationType, targetAreas, finalAreas, localizedOnMap }
    : { selectedLocationType, positionOnMap, localizedOnMap };
}
